// API helper for Grahak user-update endpoint (PATCH /grahak-service/api/v1/user).
// Mirrors the implementation from nike-reloaded for parity. Used today by the
// profile-image upload flow (sends `{ imageId }` after S3 upload). Other fields
// are supported for future migration of the profile-save flow.

import { ApiHelper } from 'shared/helpers/api';
import { getGrahakUrl, ROUTES } from 'shared/api/urls';

interface UpdateUserPayload {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  firstname?: string; // lowercase variant — converted to camelCase below
  lastname?: string; // lowercase variant — converted to camelCase below
  gender?: string;
  dob?: string;
  imageId?: string;
  email?: string;
}

interface UpdateUserResponse {
  success: boolean;
  message?: string;
  data?: any;
}

const hasValue = (value: unknown): boolean => value !== undefined && value !== null;

export const updateUser = async (payload: UpdateUserPayload): Promise<UpdateUserResponse> => {
  const url = getGrahakUrl(ROUTES.UPDATE_USER);

  const requestBody: any = {};

  // Only include fields that are provided. Backend expects camelCase.
  if (hasValue(payload.fullName)) requestBody.fullName = payload.fullName;
  if (hasValue(payload.firstName)) requestBody.firstName = payload.firstName;
  if (hasValue(payload.lastName)) requestBody.lastName = payload.lastName;
  if (hasValue(payload.firstname)) requestBody.firstName = payload.firstname;
  if (hasValue(payload.lastname)) requestBody.lastName = payload.lastname;
  if (hasValue(payload.gender)) requestBody.gender = payload.gender;
  if (hasValue(payload.dob)) requestBody.dob = payload.dob;
  if (hasValue(payload.imageId)) requestBody.imageId = payload.imageId;
  if (hasValue(payload.email)) requestBody.email = payload.email;

  try {
    const response = await ApiHelper(url, {
      method: 'PATCH',
      body: requestBody,
      isBodyJSON: true,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.message || 'Failed to update user details');
    }

    const data = await response.json().catch(() => ({}));
    const normalizedStatus = typeof data?.status === 'string' ? data.status.toLowerCase() : undefined;
    const hasExplicitFailure =
      data?.success === false || ['fail', 'failed', 'error'].includes(normalizedStatus || '');

    if (hasExplicitFailure) {
      throw new Error(data?.message || 'Failed to update user details');
    }

    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to update user details',
    };
  }
};
